'use client'

import { getEntryOpensInNewTab } from '@/feature/header/services/header.utils'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Icon } from '@/ui/icons/icon/Icon'
import type { TabDefinition } from '@/ui/tabs/Tabs.client'
import { Tabs } from '@/ui/tabs/Tabs.client'
import type { MenuEntryFragment } from '@gql-generated/graphql'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { SliderTabPanel } from './SliderTabPanel.client'
import './SliderTabs.css'

const MENU_POPIN_POSITION_CLASS =
  'fixed top-[calc(var(--app-header-height-desktop)-0.75rem)] z-header w-full'

export type SliderTabsProps = {
  menuEntries: MenuEntryFragment[] | null | undefined
  isMenuOpen: boolean
  tabContentVisible: boolean
  selectedCategoryKey: string | null
  setSelectedCategoryKey: React.Dispatch<React.SetStateAction<string | null>>
  handleShowMenu: (isOpen: boolean) => void
  handleMenuPopinClose: () => void
}

export function SliderTabs({
  menuEntries,
  isMenuOpen,
  tabContentVisible,
  selectedCategoryKey,
  setSelectedCategoryKey,
  handleShowMenu,
  handleMenuPopinClose,
}: SliderTabsProps) {
  const { t } = useTranslation(['header'])
  const menuPopinParentCategoryText = t('header:menuPopinParentCategory')

  const pathname = usePathname()
  const [selectedTabId, setSelectedTabId] = useState<string | undefined>(undefined)

  const firstItemTabId = useMemo(() => {
    const firstEntry = menuEntries
      ?.filter(Boolean)
      .find(({ entries }) => entries?.filter(Boolean).length)

    return firstEntry ? `${firstEntry.name}-${firstEntry.seoContent}` : undefined
  }, [menuEntries])

  const tabContentClass = useCallback(
    () =>
      twMerge(
        'absolute -z-base w-full transform transition-transform duration-300 ease-in-out after:absolute after:inset-0 after:left-1/2 after:ml-[-50vw] after:w-screen after:bg-white desktop-lg:max-w-[1376px]',
        !isMenuOpen || !tabContentVisible ? '-translate-y-full' : 'menu-slider-open',
      ),
    [isMenuOpen, tabContentVisible],
  )

  const handleTabChange = useCallback(
    (tabId: string | undefined) => {
      handleShowMenu(tabId !== undefined)
      handleMenuPopinClose()
      setSelectedTabId(tabId)
    },
    [handleMenuPopinClose, handleShowMenu],
  )

  const sliderTabs = useMemo<TabDefinition[]>(() => {
    const tabsData: TabDefinition[] = []

    menuEntries?.filter(Boolean).forEach(({ name, entries, seoContent, ...props }, index) => {
      if (!entries) return

      const parentUrl =
        props.__typename === 'MenuEntryLink'
          ? props.url
          : props.__typename === 'MenuEntryCategory'
            ? props.slug
            : ''

      const isBrandContext = parentUrl === 'brand'
      const isProductContext = entries.some((e) => e?.__typename === 'MenuEntryCategory')
      const validEntries = entries.filter(Boolean)
      const tabId = `${name}-${seoContent}`

      const tabBase = {
        id: tabId,
        label: name!,
        ...(index === 0 ? { icon: <Icon.Menu size="sm" /> } : {}),
        onClick: () =>
          pushToGtmDataLayer({
            event: 'gaEvent',
            eventCategory: 'menu',
            eventAction: 'niveau 1',
            eventLabel: name ?? '',
          }),
      }

      if (validEntries.length > 0) {
        tabsData.push({
          ...tabBase,
          content: (
            <SliderTabPanel
              parentName={name}
              parentSeoContent={seoContent}
              entries={validEntries}
              isBrand={isBrandContext}
              isProduct={isProductContext}
              className={tabContentClass()}
              menuPopinPositionClass={MENU_POPIN_POSITION_CLASS}
              menuPopinParentCategoryText={menuPopinParentCategoryText}
              selectedCategoryKey={selectedCategoryKey}
              setSelectedCategoryKey={setSelectedCategoryKey}
              handleMenuPopinClose={handleMenuPopinClose}
            />
          ),
        })
      } else {
        tabsData.push({
          ...tabBase,
          href: props.__typename === 'MenuEntryLink' ? props.url : '#',
          opensInNewTab: getEntryOpensInNewTab(props),
        })
      }
    })

    return tabsData
  }, [
    menuEntries,
    tabContentClass,
    menuPopinParentCategoryText,
    selectedCategoryKey,
    setSelectedCategoryKey,
    handleMenuPopinClose,
  ])

  /**
   * Open the menu on the home page, close it on any other page.
   * Depends on firstItemTabId (derived from menuEntries), never on sliderTabs,
   * to avoid re-triggering this effect on every menu open/close.
   */
  useEffect(() => {
    handleTabChange(pathname === '/' ? firstItemTabId : undefined)
  }, [handleTabChange, pathname, firstItemTabId])

  return (
    <div
      className="relative hidden w-full items-start gap-5 desktop:flex"
      data-role="sliderTabs"
      data-state={selectedTabId !== undefined ? 'open' : 'closed'}
    >
      <Tabs
        variant="md"
        tabs={sliderTabs}
        aria-label={t('header:tabsLabel')}
        selectedTabId={selectedTabId}
        allowUnselect
        onTabChange={handleTabChange}
        className={twMerge(selectedTabId === undefined && '[&_[role=separator]]:border-clear')}
      />
    </div>
  )
}
